import { useState, useEffect, useCallback } from 'react';
import { inventoryService, InventoryItem } from '../services/inventoryService';
import { useAuth } from '../context/AuthContext';

export const useInventory = () => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { items: data, error: fetchError } = await inventoryService.getInventoryItems(businessId || undefined);

      if (fetchError) {
        throw fetchError;
      }

      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    // Only fetch inventory when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchInventory();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setItems([]);
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, fetchInventory]);

  const refreshInventory = () => {
    fetchInventory();
  };

  return {
    items,
    loading,
    error,
    refreshInventory,
  };
};

export const useInventoryItem = (id: string | null) => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    // Only fetch item when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      const fetchItem = async () => {
        try {
          setLoading(true);
          setError(null);
          const { item: data, error: fetchError } = await inventoryService.getInventoryItemById(id, businessId || undefined);

          if (fetchError) {
            throw fetchError;
          }

          setItem(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch inventory item');
        } finally {
          setLoading(false);
        }
      };

      fetchItem();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setItem(null);
      setLoading(false);
    }
  }, [id, authLoading, isAuthenticated, businessId]);

  return {
    item,
    loading,
    error,
  };
};